import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../../components/PageShell';
import Tabs from '../../components/Tabs';
import { useHubExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { resolveTabs } from '../../i18n/resolveTabs';
import ReadOnlyBanner from '../../components/ReadOnlyBanner';
import HrPeoplePage from './HrPeoplePage';
import HrContractsPage from './HrContractsPage';
import PeopleImportModal from './PeopleImportModal';
import HrGeneralSettingsPage from './HrGeneralSettingsPage';
import HrAnnualExtrasPage from './HrAnnualExtrasPage';
import HrExpensesPage from './HrExpensesPage';
import HrReceivedIndemnitiesPage from './HrReceivedIndemnitiesPage';

/* Contrats et frais — hub à onglets (gabarit unique PageShell framed + Tabs) :
   TOUT ce que l'entreprise s'engage à payer par contrat, et ce qui s'y ajoute.
     • Collaborateurs / Contrats = la source (maître-détail + contrats versionnés)
     • Détail des charges        = HrSynthesisPage (coûts par personne, année N)
     • Extras / Frais / Indemnités reçues = les compléments
   L'Effectif (grille mensuelle des taux) est parti dans Chiffres clés le
   2026-08-21 au soir : c'est une lecture, pas une saisie. Sous-pages en mode
   embedded ; année globale ; unité CHF/kCHF portée par le hub, par tenant. */

/* Réorganisation 2026-08-21 (matin) : la Synthèse (ex-onglet « projection »)
   devient l'ACCUEIL du module ; cette section récupère les trois onglets de
   saisie qui alimentent le détail des charges (extras, frais, indemnités).
   Le soir, la section absorbe Collaborateurs + Contrats (l'ex-section
   « Contrats ») et cède l'Effectif à Chiffres clés — elle devient « Contrats
   et frais » : TOUT ce que l'entreprise s'engage à payer par contrat, et ce
   qui s'y ajoute. La source d'abord, ses coûts ensuite ; `sep` marque le
   passage de la saisie du contrat à la lecture des charges. */
const TAB_DEFS = [
  { id: 'people',     labelKey: 'people:data.tabs.people' },
  { id: 'contracts',  labelKey: 'people:data.tabs.contracts' },
  /* (« Détail des charges » est devenu sa propre section, dans Piloter, le
     2026-08-27 : quatre lectures valent mieux qu'un onglet à puces.) */
  { id: 'extras',     labelKey: 'people:personnel.tabs.extras', sep: true },
  { id: 'expenses',   labelKey: 'people:personnel.tabs.expenses' },
  { id: 'received',   labelKey: 'people:personnel.tabs.received' },
  /* Autres paramètres (13e/14e salaire), venu de People › Paramètres le
     2026-08-27 : c'est un réglage du CONTRAT, il se lit à côté de ce qu'il
     chiffre. */
  { id: 'general',    labelKey: 'people:data.tabs.general', setting: true, sep: true },
];

export default function PersonnelHub({
  activeTenant, setPage, addToast, year, onYearChange, onShowPeoplePrint,
  onTenantUpdated, canWrite = true, intent = null, onIntentConsumed,
  only = null, title = null,
}) {
  const { t } = useTranslation();
  /* `only` : le hub sert alors une SECTION réduite à cette liste d'onglets
     (2026-09-01) — « Salaires » porte ce qui se verse, « Frais et indemnités »
     ce qui se rembourse. Un seul composant, deux adresses. */
  const keep = only == null ? null : (Array.isArray(only) ? only : [only]);
  const defs = keep ? TAB_DEFS.filter(d => keep.includes(d.id)) : TAB_DEFS;
  const [tab, setTab] = useState((keep && keep[0]) || 'people');
  React.useEffect(() => { if (keep && !keep.includes(tab)) setTab(keep[0]); }, [keep, tab]);
  const unit = 'chf';
  const TABS = resolveTabs(defs, t);
  // Import + création rapide — machinerie reprise de l'ex-section « Contrats »
  // avec ses onglets (raccourcis de l'accueil People compris).
  const [showImport, setShowImport] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [addRequest, setAddRequest] = useState(false);

  /* INTENTION venue d'ailleurs (raccourcis de l'accueil People) : { tab,
     action } — bon onglet, et s'il y a lieu la bonne modale. Consommée une
     fois puis effacée par le parent. */
  useEffect(() => {
    if (!intent) return;
    if (intent.tab) setTab(intent.tab);
    if (intent.action === 'add') setAddRequest(true);
    if (intent.action === 'import') setShowImport(true);
    if (onIntentConsumed) onIntentConsumed();
  }, [intent]);  // eslint-disable-line

  // Rendu d'un onglet — réutilisé à l'écran (unité du hub) et pour l'export Excel
  // (unité forcée à CHF). `u` = unité effective.
  const renderTab = (id, forExport = false) => {
    const u = forExport ? 'chf' : unit;
    switch (id) {
      case 'people':     return <HrPeoplePage embedded activeTenant={activeTenant} setPage={setPage} year={year} openAdd={addRequest} onAddConsumed={() => setAddRequest(false)} />;
      case 'contracts':  return <HrContractsPage embedded activeTenant={activeTenant} setPage={setPage} year={year} />;
      case 'general':    return <HrGeneralSettingsPage activeTenant={activeTenant} addToast={addToast} onTenantUpdated={onTenantUpdated} />;
      case 'extras':     return <HrAnnualExtrasPage embedded activeTenant={activeTenant} setPage={setPage} year={year} onYearChange={onYearChange} />;
      case 'expenses':   return <HrExpensesPage embedded activeTenant={activeTenant} setPage={setPage} year={year} onYearChange={onYearChange} />;
      case 'received':   return <HrReceivedIndemnitiesPage embedded activeTenant={activeTenant} setPage={setPage} year={year} onYearChange={onYearChange} />;
      default: return null;
    }
  };

  // Exports (PDF si le parent sait l'ouvrir · Excel) → registre, rendus par
  // l'icône télécharger de la barre supérieure. La rangée d'onglets ne garde
  // que ce qui agit sur l'écran : la bascule d'unité.
  const excel = useHubExcelExport({
    tabs: TABS.map(tb => ({ label: tb.label, render: () => renderTab(tb.id, true) })),
    fileBase: t('people:personnel.fileBase'),
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  useRegisterPageExports(useMemo(() => ([
    ...(onShowPeoplePrint ? [{ id: 'pdf', label: 'PDF', run: () => onShowPeoplePrint(true) }] : []),
    { id: 'excel', label: 'Excel', run: excel.run },
  ]), [excel.run, onShowPeoplePrint]));

  /* La bascule CHF/kCHF a quitté la ligne d'onglets le 2026-08-27 : depuis que
     « Détail des charges » est parti dans sa propre section, cette section ne
     porte plus de grands montants — des salaires et des frais se lisent au
     franc. L'unité reste à CHF pour l'écran comme pour l'export. */
  const viewControls = (
    <button type="button" className="hr-tabaside-btn" onClick={() => setShowImport(true)}>
      Importer
    </button>
  );

  return (
    <PageShell
      framed
      title={title || t('people:personnel.title')}
      subtitle={title ? null : t('people:personnel.subtitle')}
    >
      {!canWrite && <ReadOnlyBanner scopeLabel={t('nav:scopes.people')} />}
      {TABS.length > 1 && <Tabs tabs={TABS} active={tab} onChange={setTab} aside={viewControls} />}

      <div className="page-shell__panel" key={reloadKey}>
        {renderTab(tab)}
      </div>
      {excel.stage}

      {showImport && (
        <PeopleImportModal
          tenantId={activeTenant.id}
          year={year}
          addToast={addToast}
          onImported={() => setReloadKey(k => k + 1)}
          onAddPerson={() => { setTab('people'); setAddRequest(true); }}
          onClose={() => setShowImport(false)}
        />
      )}
    </PageShell>
  );
}

